import { ValuesType } from 'utility-types';

import { GAME_MODE, PlayerSetup, SongPreview } from '~/interfaces';
import Listener from '~/modules/utils/listener';

/** The song settings a queued entry carries, so a duet can sit between two solos untouched. */
export interface SongQueueItemOverride {
  mode: ValuesType<typeof GAME_MODE>;
  tolerance: number;
  players: PlayerSetup[];
}

export interface SongQueueItem {
  song: SongPreview;
  override?: SongQueueItemOverride;
  /** Name of the phone that queued it, or undefined when it was added on the game machine. */
  addedBy?: string;
  addedById?: string;
}

/**
 * The party's running order. Deliberately in memory only, like the roster it plays with: a queue is
 * a property of the evening, and a stale one left over from yesterday would start playing by
 * itself the next time somebody opens the game.
 */
class SongQueueManager extends Listener<[SongQueueItem[]]> {
  private queue: SongQueueItem[] = [];

  public getQueue = (): readonly SongQueueItem[] => this.queue;

  public contains = (songId: string) => this.queue.some((item) => item.song.id === songId);

  /** Queues a song, unless it is already waiting - a party does not want it twice by accident. */
  public add = (song: SongPreview, override?: SongQueueItemOverride, addedBy?: string, addedById?: string) => {
    if (this.contains(song.id)) return false;

    this.queue = [...this.queue, { song, override, addedBy, addedById }];
    this.onUpdate(this.queue);

    return true;
  };

  public remove = (songId: string) => {
    const remaining = this.queue.filter((item) => item.song.id !== songId);
    if (remaining.length === this.queue.length) return;

    this.queue = remaining;
    this.onUpdate(this.queue);
  };

  /** Takes the next song off the queue. */
  public popNext = (): SongQueueItem | undefined => {
    const [next, ...rest] = this.queue;
    if (!next) return undefined;

    this.queue = rest;
    this.onUpdate(this.queue);

    return next;
  };

  public move = (songId: string, direction: -1 | 1) => {
    const from = this.queue.findIndex((item) => item.song.id === songId);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= this.queue.length) return;

    const reordered = [...this.queue];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

    this.queue = reordered;
    this.onUpdate(this.queue);
  };

  public clear = () => {
    if (this.queue.length === 0) return;

    this.queue = [];
    this.onUpdate(this.queue);
  };

  /**
   * Roughly how long the queue will take, in seconds. `null` when nothing queued has a known
   * duration - which is not the same as 0, and reads very differently to a player.
   */
  public getTotalDuration = (): number | null => {
    if (this.queue.length === 0) return 0;

    const known = this.queue.map((item) => item.song.duration).filter((duration): duration is number => !!duration);

    return known.length === 0 ? null : Math.round(known.reduce((total, duration) => total + duration, 0));
  };
}

const SongQueue = new SongQueueManager();

export default SongQueue;
